import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Line, ComposedChart,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Grid, Sub, Table } from '../../performance-operations/styles';
import { formatNumber, usePanel } from '../../performance-operations/config';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import { fetchWorkspaceDistribution } from './config';

// Analiz E08 "Dağılım" — önce ve sonra puan dağılımı, AYNI çalışan kümesiyle eşleştirilmiş karşılaştırma (yalnız iki hali de olan
// kişiler; eşleşmeyen sayı ayrıca). Oturum kapalıysa "sonra" onaylı son, değilse öneri (etiketli). Zorunlu dağılım YOK: oturumun
// referans hedefleri yalnız açıklayıcı katman olarak açılır (varsayılan kapalı). Tablo alternatifi var.
export default function WorkspaceDistributionTab({ sessionId, refreshKey }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const panel = usePanel(() => fetchWorkspaceDistribution(sessionId), [sessionId, refreshKey], !!sessionId);
  const d = panel.data;
  const afterLabel = d?.mode === 'approved' ? t('performance.calibrationWorkspace.series.approved') : t('performance.calibrationWorkspace.series.proposal');
  const rows = (d?.levels || []).map((l) => ({
    key: l.levelId, label: l.label, before: l.beforePct ?? 0, after: l.afterPct ?? 0, reference: l.referencePct,
    beforeN: l.before, afterN: l.after,
  }));
  const hasReference = rows.some((r) => r.reference != null);
  const pct = (v) => (v == null ? '-' : `${formatNumber(v, lang)}%`);

  return (
    <Grid data-tab="distribution">
      <PanelCard
        panelKey="distribution" title={t('performance.calibrationWorkspace.panel.distribution')} span={12}
        loading={panel.loading} error={panel.error} onRetry={panel.retry}
        empty={!!d && d.matchedN === 0} emptyText={t('performance.calibrationWorkspace.noMatched')}
        tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
        actions={hasReference ? (
          <CheckboxInput label={t('performance.calibrationWorkspace.showReference')} checked={showReference} onChange={setShowReference} />
        ) : null}
      >
        <Sub data-meta="matched">{t('performance.calibrationWorkspace.matchedNote', { n: d?.matchedN ?? 0, unmatched: d?.unmatchedN ?? 0 })}</Sub>
        {table ? (
          <Table>
            <thead>
              <tr>
                <th>{t('performance.calibrationWorkspace.col.level')}</th>
                <th>{t('performance.calibrationWorkspace.series.before')}</th>
                <th>{afterLabel}</th>
                {showReference ? <th>{t('performance.calibrationWorkspace.series.reference')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-row={r.key}>
                  <td>{r.label}</td>
                  <td>{`${r.beforeN} (${pct(r.before)})`}</td>
                  <td>{`${r.afterN} (${pct(r.after)})`}</td>
                  {showReference ? <td>{pct(r.reference)}</td> : null}
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              {showReference ? (
                <ComposedChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: theme.metrics.font.size.xs }} />
                  <YAxis unit="%" tick={{ fontSize: theme.metrics.font.size.xs }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
                  <Bar dataKey="before" name={t('performance.calibrationWorkspace.series.before')} fill={theme.colors.text.muted} />
                  <Bar dataKey="after" name={afterLabel} fill={theme.colors.primary.main} />
                  <Line dataKey="reference" name={t('performance.calibrationWorkspace.series.reference')} stroke={theme.colors.auxiliar.warning} dot />
                </ComposedChart>
              ) : (
                <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: theme.metrics.font.size.xs }} />
                  <YAxis unit="%" tick={{ fontSize: theme.metrics.font.size.xs }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
                  <Bar dataKey="before" name={t('performance.calibrationWorkspace.series.before')} fill={theme.colors.text.muted} />
                  <Bar dataKey="after" name={afterLabel} fill={theme.colors.primary.main} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
        <Sub>{t('performance.calibrationWorkspace.noForcedDistribution')}</Sub>
      </PanelCard>
    </Grid>
  );
}
